// Controle de combustíveis — notas de abastecimento do cartão Ticket Log.
//
// O Ticket Log é pré-pago: cada Pix de crédito é uma linha do Contas a Pagar.
// Desde 07/10/2026 o crédito é dinheiro ADIANTADO (saldo livre), e cada nota do
// posto CONFERIDA aqui é descontada dele e vira despesa no mês do abastecimento,
// no centro de custo da nota. O saldo do cartão fica em destaque e avisa abaixo
// de R$ 1.000. Esta tela não faz conta nenhuma (docs/combustivel.md).
//
// Tela limpa (pedido do Lucas, 01/10/2026): sem cabeçalho empilhado, cada
// número uma vez só, conteúdo logo abaixo dos filtros.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Alert, App as AntApp, Button, DatePicker, Form, Image, Input, InputNumber, Modal, Segmented, Select,
  Space, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd';
import { CheckCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { fmtDataBr } from '../utils/dataFmt';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';

const { Text } = Typography;
// Linha do rateio do crédito com o que ainda não foi usado (mobile-engine/combustivel.js).
const SALDO_A_CONSUMIR = 'Saldo a consumir';

type Status = 'aguardando_upload' | 'aguardando_revisao' | 'aprovado' | 'rejeitado';

interface Nota {
  id: number;
  email_usuario: string;
  autor_nome: string | null;
  origem: 'app' | 'erp';
  contrato_id: string | null;
  centro_custo_codigo: string | null;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  centro_custo_nome: string | null;
  veiculo_patrimonio_id: string | null;
  veiculo_nome: string | null;
  km_odometro: number | null;
  km_anterior: number | null;
  tem_arquivo: boolean;
  comprovante_content_type: string | null;
  valor: string | number | null;
  data_abastecimento: string | null;
  litros: string | number | null;
  combustivel: string | null;
  posto: string | null;
  observacao: string | null;
  ai_extracao: { erro_leitura?: string; data_alerta?: { motivo: string } | null; placa?: string | null; km?: number | null } | null;
  possivel_duplicata_de: number | null;
  status: Status;
  revisado_por: string | null;
  motivo_rejeicao: string | null;
  comentario_financeiro: string | null;
  created_at: string;
}

interface Credito {
  id: string;
  data: string;
  descricao: string | null;
  valor: number;
  com_nota: number;
  sem_nota: number;
  notas: number;
  linhas: { centro: string; nome: string; valor: number }[];
  em_dia: boolean;
  /** saldo que o cartão já tinha no corte (não é lançamento do Contas a Pagar) */
  abertura?: boolean;
}

interface Painel {
  corte: string;
  saldo?: { valor: number; limite: number; baixo: boolean };
  creditos: Credito[];
  totais: {
    credito: number; com_nota: number; sem_nota: number; aguardando_credito: number;
    pendentes: number; valor_pendente: number; anteriores_ao_controle: number; valor_anteriores: number;
  };
  veiculos: {
    veiculo_id: string; veiculo_nome: string; abastecimentos: number; valor: number; litros: number;
    km_rodados: number | null; km_por_litro: number | null;
  }[];
  fora_de_sincronia: number;
  /** previsões de combustível do Conta Azul, abatidas pelas notas do mês (07/10/2026) */
  previsoes?: { periodo: string; previsto: number; notas: number; valor: number }[];
}

interface Veiculo { id: string; descricao: string }
interface Opcao { value: string; label: string }

const STATUS_TAG: Record<Status, { color: string; txt: string }> = {
  aguardando_upload:  { color: 'default', txt: 'Envio incompleto' },
  aguardando_revisao: { color: 'warning', txt: 'A conferir' },
  aprovado:           { color: 'success', txt: 'Conferida' },
  rejeitado:          { color: 'error',   txt: 'Devolvida' },
};

const brl = (v: string | number | null | undefined) =>
  v == null || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const num = (v: string | number | null | undefined, casas = 1) =>
  v == null || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: casas });
const un = (d: any) => (d?.body ? JSON.parse(d.body) : d);
const erroDe = (e: any) => e?.response?.data?.error || e?.message || 'Não foi possível concluir';
const chaveDestino = (n: Pick<Nota, 'contrato_id' | 'centro_custo_codigo'>) =>
  (n.contrato_id ? `contrato:${n.contrato_id}` : n.centro_custo_codigo ? `centro:${n.centro_custo_codigo}` : undefined);
const payloadDestino = (chave?: string | null) => ({
  contrato_id: chave?.startsWith('contrato:') ? chave.slice(9) : null,
  centro_custo_codigo: chave?.startsWith('centro:') ? chave.slice(7) : null,
});

async function api<T = any>(method: 'get' | 'post' | 'patch' | 'delete', rota: string, corpo?: any, params?: any): Promise<T> {
  const token = await getToken();
  const r = await axios.request({
    method, url: `${API_URL}/mobile/combustivel${rota}`, headers: { Authorization: token },
    data: corpo, params, timeout: 60000,
  });
  return un(r.data) as T;
}

function FinanceiroCombustiveisInner() {
  const { message, modal } = AntApp.useApp();
  const [visao, setVisao] = useState<'notas' | 'creditos' | 'veiculos'>('notas');
  const [filtro, setFiltro] = useState<string>('aguardando_revisao');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [notas, setNotas] = useState<Nota[]>([]);
  const [painel, setPainel] = useState<Painel | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [destinos, setDestinos] = useState<Opcao[]>([]);
  const [veiculos, setVeiculos] = useState<Opcao[]>([]);

  const [aberta, setAberta] = useState<Nota | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [lancando, setLancando] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [form] = Form.useForm();
  const [formNovo] = Form.useForm();
  const pedido = useRef(0);
  const kmNoForm = Form.useWatch('km_odometro', form) as number | null | undefined;

  useEffect(() => {
    const t = window.setTimeout(() => setBuscaAplicada(busca.trim()), 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  const carregarNotas = useCallback(async () => {
    const id = ++pedido.current;
    setCarregando(true);
    setErro(null);
    try {
      const params: Record<string, string> = {};
      if (filtro !== 'todas') params.status = filtro;
      if (buscaAplicada) params.q = buscaAplicada;
      const d = await api<{ abastecimentos: Nota[] }>('get', '/abastecimentos', undefined, params);
      if (id === pedido.current) setNotas(d.abastecimentos || []);
    } catch (e) {
      if (id === pedido.current) setErro(erroDe(e));
    } finally {
      if (id === pedido.current) setCarregando(false);
    }
  }, [filtro, buscaAplicada]);

  // O painel também é quem percebe o Pix novo que entrou no Contas a Pagar
  // depois das notas: se o rateio gravado não bate com as notas conferidas, a
  // tela pede ao servidor para regravar — sem ninguém precisar apertar nada.
  const carregarPainel = useCallback(async () => {
    try {
      let p = await api<Painel>('get', '/painel');
      if (p.fora_de_sincronia > 0) {
        const s = await api<{ creditos_atualizados: number }>('post', '/sincronizar');
        if (s.creditos_atualizados > 0) {
          message.info(`Rateio atualizado em ${s.creditos_atualizados} crédito(s) do Ticket Log.`);
        }
        p = await api<Painel>('get', '/painel');
      }
      setPainel(p);
    } catch (e) {
      setErro(erroDe(e));
    }
  }, [message]);

  useEffect(() => { carregarNotas(); }, [carregarNotas]);
  useEffect(() => { carregarPainel(); }, [carregarPainel]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/mobile/contratos`, { headers: { Authorization: token } });
        const d = un(r.data);
        setDestinos([
          ...(d?.contratos || []).map((c: any) => ({ value: `contrato:${c.id}`, label: `${c.numero || '—'} — ${c.titulo || ''}` })),
          ...(d?.centros_custo || []).map((c: any) => ({ value: `centro:${c.codigo_canonical}`, label: `${c.nome_completo} (${c.codigo_canonical}) — indireto` })),
        ]);
      } catch { /* o campo fica com o destino atual */ }
      try {
        const d = await api<{ veiculos: Veiculo[] }>('get', '/veiculos');
        setVeiculos((d.veiculos || []).map((v) => ({ value: v.id, label: v.descricao })));
      } catch { /* idem */ }
    })();
  }, []);

  const recarregar = useCallback(() => { carregarNotas(); carregarPainel(); }, [carregarNotas, carregarPainel]);

  const abrir = useCallback(async (n: Nota) => {
    setAberta(n);
    setFotoUrl(null);
    if (!n.tem_arquivo) return;
    try {
      const d = await api<{ comprovante_url: string | null }>('get', `/abastecimentos/${n.id}`);
      setFotoUrl(d.comprovante_url);
    } catch { /* a conferência segue sem a imagem */ }
  }, []);

  // Os campos são preenchidos DEPOIS de o formulário existir na tela (o modal
  // só o monta ao abrir) e antes da pintura — nunca sobra valor da nota anterior.
  useLayoutEffect(() => {
    if (!aberta) return;
    form.setFieldsValue({
      valor: aberta.valor != null ? Number(aberta.valor) : null,
      data_abastecimento: aberta.data_abastecimento ? dayjs(aberta.data_abastecimento) : null,
      litros: aberta.litros != null ? Number(aberta.litros) : null,
      posto: aberta.posto || '',
      destino: chaveDestino(aberta),
      veiculo_patrimonio_id: aberta.veiculo_patrimonio_id || undefined,
      km_odometro: aberta.km_odometro,
      comentario_financeiro: aberta.comentario_financeiro || '',
    });
  }, [aberta, form]);

  // Chegou pelo sino: abre a nota avisada (uma vez).
  const sinoFeito = useRef(false);
  useEffect(() => {
    if (sinoFeito.current || !notas.length) return;
    const alvo = sessionStorage.getItem('combustiveis_abrir_id');
    if (!alvo) return;
    const n = notas.find((x) => String(x.id) === alvo);
    if (!n) return;
    sessionStorage.removeItem('combustiveis_abrir_id');
    sinoFeito.current = true;
    abrir(n);
  }, [notas, abrir]);

  const camposDoForm = () => {
    const v = form.getFieldsValue();
    return {
      valor: v.valor ?? null,
      data_abastecimento: v.data_abastecimento ? v.data_abastecimento.format('YYYY-MM-DD') : null,
      litros: v.litros ?? null,
      posto: v.posto || null,
      veiculo_patrimonio_id: v.veiculo_patrimonio_id || null,
      km_odometro: v.veiculo_patrimonio_id ? (v.km_odometro ?? null) : null,
      comentario_financeiro: v.comentario_financeiro || null,
      // O destino só vai quando MUDOU: numa nota já conferida, mandar o mesmo
      // destino faria o servidor recalcular o centro de custo pelo cadastro de hoje.
      ...(aberta && v.destino === chaveDestino(aberta) ? {} : payloadDestino(v.destino)),
    };
  };

  async function agir(acao: 'aprovar' | 'salvar' | 'reabrir', n: Nota) {
    setSalvando(true);
    try {
      if (acao === 'reabrir') {
        await api('post', `/abastecimentos/${n.id}/reabrir`);
        message.success('Nota reaberta — voltou para a fila e saiu do rateio.');
      } else if (acao === 'salvar') {
        await api('patch', `/abastecimentos/${n.id}`, camposDoForm());
        message.success('Alterações salvas.');
      } else {
        const r = await api<{ aguardando_credito: number | null; anterior_ao_controle: boolean;
          saldo?: { valor: number; limite: number; baixo: boolean; avisou: boolean } }>(
          'post', `/abastecimentos/${n.id}/aprovar`, camposDoForm());
        if (r.anterior_ao_controle) {
          message.warning('Nota conferida, mas é anterior ao controle automático: o crédito daquela época segue com o rateio feito à mão.', 8);
        } else if (r.aguardando_credito && r.aguardando_credito > 0) {
          message.warning(`Nota conferida. ${brl(r.aguardando_credito)} passam do crédito lançado — o saldo do cartão ficou negativo até o próximo Pix do Ticket Log entrar no Contas a Pagar.`, 8);
        } else if (r.saldo && r.saldo.baixo) {
          message.warning(`Nota conferida e descontada. Saldo do cartão: ${brl(r.saldo.valor)} — abaixo de ${brl(r.saldo.limite)}, hora de recarregar.`, 8);
        } else {
          message.success('Nota conferida — descontada do saldo do cartão e lançada como despesa no mês do abastecimento.');
        }
      }
      setAberta(null);
      recarregar();
    } catch (e) {
      message.error(erroDe(e));
    } finally {
      setSalvando(false);
    }
  }

  function pedirMotivo(titulo: string, rotulo: string, okText: string, minimo: number, feito: (motivo: string) => Promise<void>) {
    let texto = '';
    modal.confirm({
      title: titulo,
      width: 480,
      content: (
        <div>
          <Text style={{ display: 'block', marginBottom: 4 }}>{rotulo}</Text>
          <Input.TextArea rows={3} onChange={(e) => { texto = e.target.value; }} />
        </div>
      ),
      okText,
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        if (texto.trim().length < minimo) {
          message.error(`Escreva o motivo (mínimo ${minimo} letras).`);
          throw new Error('motivo');
        }
        try {
          await feito(texto.trim());
          setAberta(null);
          recarregar();
        } catch (e) {
          message.error(erroDe(e));
          throw e;
        }
      },
    });
  }

  async function lancar(conferirJa: boolean) {
    let v: any;
    try { v = await formNovo.validateFields(); } catch { return; }
    const base = {
      valor: v.valor ?? null,
      data_abastecimento: v.data_abastecimento ? v.data_abastecimento.format('YYYY-MM-DD') : null,
      litros: v.litros ?? null,
      posto: v.posto || null,
      veiculo_patrimonio_id: v.veiculo_patrimonio_id || null,
      km_odometro: v.veiculo_patrimonio_id ? (v.km_odometro ?? null) : null,
      observacao: v.observacao || null,
      ...payloadDestino(v.destino),
    };
    setSalvando(true);
    try {
      if (arquivo) {
        // Com a foto: sobe, a IA lê, e a nota abre para conferência como as do app.
        const tipo = arquivo.type || (/\.pdf$/i.test(arquivo.name) ? 'application/pdf' : 'image/jpeg');
        const u = await api<{ abastecimento_id: number; upload_url: string }>(
          'post', '/abastecimentos/upload-url', { ...base, content_type: tipo, origem: 'erp' });
        const put = await fetch(u.upload_url, { method: 'PUT', body: arquivo, headers: { 'Content-Type': tipo } });
        if (!put.ok) throw new Error(`O arquivo não subiu (${put.status}). Tente de novo.`);
        await api('post', `/abastecimentos/${u.abastecimento_id}/processar`);
        if (base.valor != null || base.data_abastecimento || base.litros != null || base.posto) {
          await api('patch', `/abastecimentos/${u.abastecimento_id}`, {
            ...(base.valor != null ? { valor: base.valor } : {}),
            ...(base.data_abastecimento ? { data_abastecimento: base.data_abastecimento } : {}),
            ...(base.litros != null ? { litros: base.litros } : {}),
            ...(base.posto ? { posto: base.posto } : {}),
          });
        }
        message.success('Nota lançada — confira e confirme.');
        setLancando(false);
        setFiltro('aguardando_revisao');
        const d = await api<{ abastecimento: Nota }>('get', `/abastecimentos/${u.abastecimento_id}`);
        recarregar();
        abrir(d.abastecimento);
      } else {
        if (base.valor == null || !base.data_abastecimento) {
          message.error('Sem a foto, informe pelo menos o valor e a data da nota.');
          return;
        }
        await api('post', '/abastecimentos/manual', { ...base, aprovar: conferirJa });
        message.success(conferirJa ? 'Nota lançada e conferida — o rateio já foi atualizado.' : 'Nota lançada na fila.');
        setLancando(false);
        recarregar();
      }
    } catch (e) {
      message.error(erroDe(e));
    } finally {
      setSalvando(false);
    }
  }

  const colunasNotas = [
    { title: 'Data', key: 'data', width: 105,
      render: (_: unknown, n: Nota) => (n.data_abastecimento ? fmtDataBr(n.data_abastecimento) : <Text type="secondary">sem data</Text>) },
    { title: 'Quem abasteceu', key: 'quem', ellipsis: true,
      render: (_: unknown, n: Nota) => (
        <span>{n.autor_nome || n.email_usuario}{n.origem === 'erp' && <Text type="secondary"> · lançada aqui</Text>}</span>) },
    { title: 'Veículo', key: 'veiculo', ellipsis: true,
      render: (_: unknown, n: Nota) => (n.veiculo_nome
        ? <span>{n.veiculo_nome}{n.km_odometro != null && <Text type="secondary"> · {num(n.km_odometro, 0)} km</Text>}</span>
        : <Text type="secondary">{n.observacao || 'outro'}</Text>) },
    { title: 'Centro de custo', key: 'cc', ellipsis: true,
      render: (_: unknown, n: Nota) => n.contrato_numero || n.centro_custo_nome || <Text type="secondary">—</Text> },
    { title: 'Posto', dataIndex: 'posto', key: 'posto', ellipsis: true,
      render: (v: string | null) => v || <Text type="secondary">—</Text> },
    { title: 'Litros', key: 'litros', align: 'right' as const, width: 80, render: (_: unknown, n: Nota) => num(n.litros) },
    { title: 'Valor', key: 'valor', align: 'right' as const, width: 115,
      render: (_: unknown, n: Nota) => <strong>{brl(n.valor)}</strong> },
    { title: 'Situação', key: 'status', width: 190,
      render: (_: unknown, n: Nota) => (
        <Space size={4} wrap>
          <Tag color={STATUS_TAG[n.status]?.color}>{STATUS_TAG[n.status]?.txt || n.status}</Tag>
          {n.possivel_duplicata_de && n.status !== 'rejeitado' && (
            <Tooltip title={`Mesmo valor e mesma data da nota #${n.possivel_duplicata_de}`}><Tag color="volcano">repetida?</Tag></Tooltip>)}
        </Space>) },
    { title: '', key: 'acao', width: 95,
      render: (_: unknown, n: Nota) => (
        <Button size="small" type="link" onClick={() => abrir(n)}>{n.status === 'aguardando_revisao' ? 'Conferir' : 'Abrir'}</Button>) },
  ];

  const colunasCreditos = [
    { title: 'Crédito (Pix)', key: 'data', width: 120,
      render: (_: unknown, c: Credito) => (c.abertura ? <Text type="secondary">até {fmtDataBr(c.data)}</Text> : fmtDataBr(c.data)) },
    { title: 'Descrição', dataIndex: 'descricao', key: 'descricao', ellipsis: true },
    { title: 'Valor', key: 'valor', align: 'right' as const, width: 130, render: (_: unknown, c: Credito) => brl(c.valor) },
    { title: 'Com nota', key: 'com', align: 'right' as const, width: 130, render: (_: unknown, c: Credito) => brl(c.com_nota) },
    { title: 'Saldo (a usar)', key: 'sem', align: 'right' as const, width: 150,
      render: (_: unknown, c: Credito) => (c.sem_nota > 0 ? brl(c.sem_nota) : <Text type="secondary">usado por inteiro</Text>) },
    { title: 'Usado em', key: 'rateio',
      render: (_: unknown, c: Credito) => (
        <Space size={[4, 4]} wrap>
          {c.linhas.filter((l) => l.centro !== SALDO_A_CONSUMIR).map((l) => (
            <Tooltip key={l.centro} title={l.nome}><Tag>{l.centro} · {brl(l.valor)}</Tag></Tooltip>))}
        </Space>) },
  ];

  const colunasVeiculos = [
    { title: 'Veículo', dataIndex: 'veiculo_nome', key: 'v' },
    { title: 'Abastecimentos', dataIndex: 'abastecimentos', key: 'n', align: 'right' as const, width: 140 },
    { title: 'Litros', key: 'l', align: 'right' as const, width: 110, render: (_: unknown, v: Painel['veiculos'][number]) => num(v.litros) },
    { title: 'Valor', key: 'r', align: 'right' as const, width: 130, render: (_: unknown, v: Painel['veiculos'][number]) => brl(v.valor) },
    { title: 'Km rodados', key: 'km', align: 'right' as const, width: 120,
      render: (_: unknown, v: Painel['veiculos'][number]) => (v.km_rodados != null ? num(v.km_rodados, 0) : <Text type="secondary">—</Text>) },
    { title: 'Média (km/l)', key: 'kml', align: 'right' as const, width: 120,
      render: (_: unknown, v: Painel['veiculos'][number]) => (v.km_por_litro != null ? num(v.km_por_litro) : (
        <Tooltip title="Precisa de pelo menos 2 abastecimentos com km e litros"><Text type="secondary">—</Text></Tooltip>)) },
  ];

  const pend = painel?.totais.pendentes ?? 0;
  const kmMenor = !!aberta && aberta.km_anterior != null && kmNoForm != null && Number(kmNoForm) < Number(aberta.km_anterior);
  const ehPdf = /pdf/i.test(aberta?.comprovante_content_type || '');
  const conferida = aberta?.status === 'aprovado';

  const camposDaNota = useMemo(() => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Form.Item label="Valor (R$)" name="valor"><InputNumber {...PROPS_DINHEIRO_BR} min={0.01} style={{ width: '100%' }} /></Form.Item>
        <Form.Item label="Data do abastecimento" name="data_abastecimento">
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item label="Litros" name="litros"><InputNumber decimalSeparator="," min={0.1} style={{ width: '100%' }} /></Form.Item>
      </div>
      <Form.Item label="Posto" name="posto"><Input maxLength={200} /></Form.Item>
      <Form.Item label="Centro de custo" name="destino" rules={[{ required: true, message: 'Escolha o centro de custo' }]}>
        <Select showSearch optionFilterProp="label" options={destinos} placeholder="Digite o número ou o nome do contrato" />
      </Form.Item>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        <Form.Item label="Veículo" name="veiculo_patrimonio_id">
          <Select showSearch allowClear optionFilterProp="label" options={veiculos} placeholder="Outro (barco, gerador…)" />
        </Form.Item>
        <Form.Item label="Km do painel" name="km_odometro"><InputNumber min={0} precision={0} style={{ width: '100%' }} /></Form.Item>
      </div>
    </>
  ), [destinos, veiculos]);

  return (
    <div style={{ padding: 16 }}>
      {!!painel?.saldo?.baixo && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message={`Saldo do cartão Ticket Log abaixo de ${brl(painel.saldo.limite)} — hora de recarregar`}
          description="Depois da recarga, lance o Pix no Contas a Pagar (fornecedor Ticket, categoria Combustíveis): ele entra como saldo novo." />
      )}
      <Tabs
        activeKey={visao}
        onChange={(k) => setVisao(k as typeof visao)}
        tabBarExtraContent={(
          <Space wrap>
            {!!painel?.saldo && (
              <Tooltip title="O que as recargas lançadas ainda não usaram, descontadas as notas conferidas. Nota que ainda não entrou no sistema não está descontada.">
                <Tag color={painel.saldo.baixo ? 'red' : 'blue'} style={{ fontSize: 14, padding: '4px 10px', margin: 0 }}>
                  Saldo do cartão: <b>{brl(painel.saldo.valor)}</b>
                </Tag>
              </Tooltip>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setArquivo(null); setLancando(true); }}>
              Lançar nota
            </Button>
            <Button icon={<ReloadOutlined />} onClick={recarregar} loading={carregando}>Atualizar</Button>
          </Space>
        )}
        items={[
          {
            key: 'notas',
            label: pend > 0 ? `Notas (${pend} a conferir)` : 'Notas',
            children: (
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap>
                  <Segmented
                    value={filtro}
                    onChange={(v) => setFiltro(v as string)}
                    options={[
                      { label: 'A conferir', value: 'aguardando_revisao' },
                      { label: 'Conferidas', value: 'aprovado' },
                      { label: 'Devolvidas', value: 'rejeitado' },
                      { label: 'Todas', value: 'todas' },
                    ]}
                  />
                  <Input.Search
                    allowClear maxLength={100} value={busca} onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar por pessoa, veículo, posto ou contrato" aria-label="Buscar notas de combustível"
                    style={{ width: 340, maxWidth: '100%' }}
                  />
                </Space>
                {erro && <Alert type="error" message={erro} />}
                <Table
                  rowKey="id" size="middle" tableLayout="fixed" dataSource={notas} columns={colunasNotas} loading={carregando}
                  pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
                  scroll={{ x: 980 }}
                  locale={{ emptyText: filtro === 'aguardando_revisao' ? 'Nenhuma nota esperando conferência' : 'Nenhuma nota neste filtro' }}
                />
              </Space>
            ),
          },
          {
            key: 'creditos',
            label: 'Créditos e saldo',
            children: (
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                {!!painel && painel.totais.aguardando_credito > 0 && (
                  <Alert
                    type="warning" showIcon
                    message={`${brl(painel.totais.aguardando_credito)} em notas conferidas passam do crédito lançado`}
                    description="Já são despesa (o combustível foi usado). São descontadas do próximo Pix do Ticket Log quando ele for lançado no Contas a Pagar (fornecedor Ticket, categoria Combustíveis)."
                  />
                )}
                <Table
                  rowKey="id" size="middle" dataSource={painel?.creditos || []} columns={colunasCreditos} loading={!painel && !erro}
                  pagination={false} scroll={{ x: 980 }}
                  locale={{ emptyText: 'Nenhum crédito do Ticket Log no controle automático ainda' }}
                  summary={() => (painel && painel.creditos.length > 1 ? (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><strong>{brl(painel.totais.credito)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><strong>{brl(painel.totais.com_nota)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right"><strong>{brl(painel.totais.sem_nota)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} />
                    </Table.Summary.Row>
                  ) : null)}
                />
                {painel && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Cada nota conferida desconta do crédito mais antigo que ainda tem saldo e vira despesa no mês do abastecimento,
                    no centro de custo dela. O saldo fica livre no crédito: é dinheiro adiantado, não despesa.
                    Créditos anteriores a {fmtDataBr(painel.corte)} foram rateados à mão e não mudam.
                    {painel.creditos.some((c) => c.abertura)
                      && ' O saldo que o cartão já tinha nessa data já foi despesa no mês do Pix antigo: as primeiras notas gastam dele e essa parte não conta de novo.'}
                    {(painel.previsoes || []).map((pv) => (
                      ` Previsão do Conta Azul de ${pv.periodo.slice(5, 7)}/${pv.periodo.slice(0, 4)}: ${brl(pv.previsto)} previstos`
                      + ` − ${brl(pv.notas)} em notas = ${brl(pv.valor)} (abatida a cada nota; apagada quando o mês fecha).`)).join('')}
                    {painel.totais.anteriores_ao_controle > 0
                      && ` Há ${painel.totais.anteriores_ao_controle} nota(s) conferida(s) dessa época (${brl(painel.totais.valor_anteriores)}) fora do rateio automático.`}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            key: 'veiculos',
            label: 'Veículos',
            children: (
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                <Table
                  rowKey="veiculo_id" size="middle" dataSource={painel?.veiculos || []} columns={colunasVeiculos}
                  loading={!painel && !erro} pagination={false} scroll={{ x: 720 }}
                  locale={{ emptyText: 'Nenhum abastecimento conferido nos últimos 90 dias' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Notas conferidas dos últimos 90 dias. A média é aproximada: km rodados entre o primeiro e o último
                  abastecimento, divididos pelos litros postos depois do primeiro.
                </Text>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={!!aberta}
        onCancel={() => setAberta(null)}
        title={aberta ? `Nota de combustível #${aberta.id} — ${aberta.autor_nome || aberta.email_usuario}` : ''}
        width="94vw"
        style={{ maxWidth: 1180, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
        destroyOnHidden
        footer={aberta ? [
          <Button key="x" onClick={() => setAberta(null)}>Fechar</Button>,
          <Button key="del" danger onClick={() => pedirMotivo('Excluir a nota', 'Motivo da exclusão', 'Excluir', 5,
            (motivo) => api('delete', `/abastecimentos/${aberta.id}`, { motivo }).then(() => { message.success('Nota excluída.'); }))}>
            Excluir
          </Button>,
          ...(conferida ? [
            <Button key="re" onClick={() => agir('reabrir', aberta)} loading={salvando}>Reabrir</Button>,
            <Button key="ok" type="primary" icon={<CheckCircleOutlined />} loading={salvando} onClick={() => agir('salvar', aberta)}>
              Salvar alterações
            </Button>,
          ] : [
            ...(aberta.status !== 'rejeitado' ? [
              <Button key="dev" danger onClick={() => pedirMotivo('Devolver a nota', 'O que a pessoa precisa corrigir? Ela recebe este texto.', 'Devolver', 5,
                (motivo) => api('post', `/abastecimentos/${aberta.id}/rejeitar`, { motivo }).then(() => { message.success('Nota devolvida — quem enviou foi avisado.'); }))}>
                Devolver
              </Button>,
            ] : []),
            <Button key="sv" onClick={() => agir('salvar', aberta)} loading={salvando}>Salvar sem confirmar</Button>,
            <Button key="ok" type="primary" icon={<CheckCircleOutlined />} loading={salvando} onClick={() => agir('aprovar', aberta)}>
              Confirmar nota
            </Button>,
          ]),
        ] : null}
      >
        {aberta && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
            <div>
              {!aberta.tem_arquivo ? (
                <Alert type="info" showIcon message="Nota lançada sem foto" />
              ) : !fotoUrl ? (
                <Text type="secondary">Carregando a nota…</Text>
              ) : ehPdf ? (
                <iframe src={fotoUrl} title="nota em PDF"
                  style={{ width: '100%', height: 'clamp(320px, calc(100vh - 260px), 760px)', border: '1px solid #eee' }} />
              ) : (
                <Image src={fotoUrl} alt="nota do posto"
                  style={{ maxWidth: '100%', maxHeight: 'clamp(320px, calc(100vh - 260px), 760px)', objectFit: 'contain' }} />
              )}
              {fotoUrl && (
                <div style={{ marginTop: 8 }}>
                  <Button size="small" onClick={() => window.open(fotoUrl, '_blank', 'noopener')}>Abrir em nova aba</Button>
                </div>
              )}
            </div>
            <div>
              {aberta.possivel_duplicata_de && aberta.status !== 'rejeitado' && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                  message={`Pode ser repetida da nota #${aberta.possivel_duplicata_de}`}
                  description="Mesmo valor na mesma data. Confira as duas antes de confirmar — nota repetida desconta o crédito duas vezes." />
              )}
              {aberta.ai_extracao?.erro_leitura && (
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                  message="A leitura automática não funcionou nesta nota"
                  description="Preencha valor, data e litros olhando para a imagem ao lado." />
              )}
              {aberta.ai_extracao?.data_alerta?.motivo && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Confira a data" description={aberta.ai_extracao.data_alerta.motivo} />
              )}
              {aberta.status === 'rejeitado' && (
                <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Nota devolvida" description={aberta.motivo_rejeicao || 'Sem motivo informado.'} />
              )}
              {aberta.observacao && (
                <Alert type="info" style={{ marginBottom: 12 }} message="Observação de quem enviou" description={aberta.observacao} />
              )}
              <Form form={form} layout="vertical" requiredMark={false} preserve={false}>
                {camposDaNota}
                {(aberta.km_anterior != null || kmMenor) && (
                  <div style={{ marginTop: -12, marginBottom: 12 }}>
                    <Text type={kmMenor ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                      {kmMenor ? 'Menor que o ' : ''}Km anterior deste veículo: {num(aberta.km_anterior, 0)}
                      {!kmMenor && kmNoForm != null && aberta.km_anterior != null && ` · rodou ${num(Number(kmNoForm) - Number(aberta.km_anterior), 0)} km desde então`}
                    </Text>
                  </div>
                )}
                <Form.Item label="Comentário do financeiro" name="comentario_financeiro" style={{ marginBottom: 0 }}>
                  <Input.TextArea rows={2} maxLength={1000} />
                </Form.Item>
              </Form>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={lancando}
        onCancel={() => setLancando(false)}
        title="Lançar nota de combustível"
        width="94vw"
        style={{ maxWidth: 720, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
        destroyOnHidden
        footer={[
          <Button key="x" onClick={() => setLancando(false)}>Cancelar</Button>,
          ...(arquivo ? [
            <Button key="ler" type="primary" loading={salvando} onClick={() => lancar(false)}>Enviar e ler a nota</Button>,
          ] : [
            <Button key="fila" loading={salvando} onClick={() => lancar(false)}>Lançar na fila</Button>,
            <Button key="ok" type="primary" icon={<CheckCircleOutlined />} loading={salvando} onClick={() => lancar(true)}>Lançar e confirmar</Button>,
          ]),
        ]}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Para a nota que chegou por fora do app (WhatsApp, e-mail). Com a foto, o sistema lê valor, data e litros;
          sem a foto, digite o valor e a data.
        </Text>
        <div style={{ marginBottom: 16 }}>
          <input
            type="file" accept="image/*,application/pdf" aria-label="Foto ou PDF da nota"
            onChange={(e) => setArquivo(e.target.files?.[0] || null)}
          />
        </div>
        <Form form={formNovo} layout="vertical" requiredMark={false} preserve={false}>
          {camposDaNota}
          <Form.Item label="Observação (quem abasteceu, para quê)" name="observacao" style={{ marginBottom: 0 }}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default function FinanceiroCombustiveis() {
  return (
    <AntApp>
      <FinanceiroCombustiveisInner />
    </AntApp>
  );
}
